// App.jsx
import { useState, useEffect, useMemo } from 'react'
import {
  HashRouter,
  Routes,
  Route,
  Link,
  useLocation
} from 'react-router-dom'
import { supabase } from './lib/supabaseClient'
import { useAuth } from './providers/AuthProvider'

// 공용 컴포넌트 & 상수/서비스
import JobPostingForm from './components/Pharmacy/JobPostingForm'
import { getMyPharmacyJobs, deleteJobPost } from './services/jobs'
import { REGIONS } from './components/shared/regions'
import AuthNav from './components/AuthNav'
import InstallPrompt from './components/InstallPrompt'
import BottomNav from './components/BottomNav'

// 페이지 컴포넌트
import Home from './pages/Home'
import Pharmacy from './pages/Pharmacy'
import Pharmacist from './pages/Pharmacist'
import Login from './pages/Login'
import SignUp from './pages/SignUp'
import VerifyEmail from './pages/VerifyEmail'
import ResetRequest from './pages/ResetRequest'
import ResetPassword from './pages/ResetPassword'
import AdminLayout from './pages/admin/AdminLayout'
import AdminReview from './pages/admin/AdminReview'
import AdminUsers from './pages/admin/AdminUsers'
import AdminJobs from './pages/admin/AdminJobs'
import AdminApplications from './pages/admin/AdminApplications'
import AdminSettings from './pages/admin/AdminSettings'

// ✅ 마이페이지
import MyPagePharmacy from './pages/MyPagePharmacy'
import MyPagePharmacist from './pages/MyPagePharmacist'

import PharmacistCalendarPage from './pages/PharmacistCalendarPage'
import PharmacistJobListPage from './pages/PharmacistJobListPage'

// ===============================================
// 0) 공통 UI
// ===============================================
const PageLayout = ({ title, children }) => (
  <div className="p-8 border border-dashed border-gray-300 bg-white shadow-lg rounded-xl">
    <h2 className="text-2xl font-semibold mb-4 text-blue-600">{title}</h2>
    {children}
  </div>
)

const InputField = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  className = '',
  id,
  children,
  isTextarea = false,
  ...rest
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    {isTextarea ? (
      <textarea
        id={id}
        className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        rows="3"
        {...rest}
      />
    ) : (
      <input
        id={id}
        className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ${className}`}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        {...rest}
      />
    )}
    {children}
  </div>
)

const ReadonlyBanner = ({ reason }) => (
  <div className="mb-4 p-3 rounded-lg text-sm border shadow bg-gray-50 text-gray-700 border-gray-200">
    {reason || '현재 페이지는 열람만 가능합니다.'}
  </div>
)

// ===============================================
// 1) 역할/프로필 동기화 + 자동 레코드 보장
//    ⚠ profiles 테이블은 RLS 때문에 여기서 INSERT/UPSERT 하지 않음
// ===============================================
async function ensureProfileRoleFromUserMetadata() {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  if (!user) return null

  const desiredRole = user.user_metadata?.role
  const desiredName = user.user_metadata?.name || null
  const desiredEmail = user.email || null
  const desiredApproval = user.user_metadata?.approval_status || null

  const { data: prof } = await supabase
    .from('profiles')
    .select('role, display_name, email, approval_status')
    .eq('user_id', user.id)
    .maybeSingle()

  // 🔹 프로필이 없으면 DB에는 손대지 않고 메타데이터 기준 role만 리턴
  if (!prof) {
    return desiredRole ?? null
  }

  // [수정] 관리자 권한 유지 로직: DB가 'admin'이면 메타데이터나 다른 로직에 의해 변하지 않도록 함
  if (prof.role === 'admin') {
    // 혹시 메타데이터가 admin이 아니면 클라이언트 측에서 메타데이터 업데이트 시도 (선택 사항)
    if (desiredRole !== 'admin') {
      console.log('Syncing metadata to match DB admin role...')
      supabase.auth.updateUser({ data: { role: 'admin' } }).then(({ error }) => {
        if (error) console.error('Failed to sync metadata to admin:', error)
      })
    }
    return 'admin'
  }

  // 변경된 값만 PATCH (UPDATE 는 RLS 허용 정책이 있다고 가정)
  // ⚠ 주의: DB의 role/approval_status가 메타데이터보다 우선이어야 함 (관리자가 DB에서 승인/변경했을 수 있음)
  const patch = {}
  // role 동기화는 막아둠 (관리자가 DB에서 role을 바꿨을 때 메타데이터가 덮어쓰지 않도록)
  // if (desiredRole && prof.role !== desiredRole) patch.role = desiredRole 
  if (desiredName && prof.display_name !== desiredName) patch.display_name = desiredName
  if (desiredEmail && prof.email !== desiredEmail) patch.email = desiredEmail

  if (Object.keys(patch).length > 0) {
    await supabase.from('profiles').update(patch).eq('user_id', user.id)
  }
  return prof.role ?? desiredRole ?? null
}

async function getCurrentRole() {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  if (!user) return null
  const { data: prof } = await supabase
    .from('profiles')
    .select('role')
    .eq('user_id', user.id)
    .maybeSingle()
  return prof?.role ?? user.user_metadata?.role ?? null
}

// role + approval_status + 이름/이메일 한 번에 조회
async function getCurrentProfileInfo() {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  if (!user) return null

  const { data: prof } = await supabase
    .from('profiles')
    .select('role, approval_status, display_name, email')
    .eq('user_id', user.id)
    .maybeSingle()

  return {
    role: prof?.role ?? user.user_metadata?.role ?? null,
    approval_status:
      prof?.approval_status ??
      user.user_metadata?.approval_status ??
      'pending',
    name: prof?.display_name ?? user.user_metadata?.name ?? null,
    email: prof?.email ?? user.email ?? null
  }
}

// pharmacists / pharmacies 테이블 자동 생성
async function ensureRoleRecords() {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  if (!user) return

  const role = await ensureProfileRoleFromUserMetadata()
  if (!role) return

  const meta = user.user_metadata || {}

  if (role === 'pharmacist') {
    const { data: existing } = await supabase
      .from('pharmacists')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!existing) {
      const {
        name,
        phone,
        postcode,
        address,
        address_detail,
        license_number,
        license_url
      } = meta

      await supabase.from('pharmacists').insert({
        user_id: user.id,
        name: name || null,
        email: user.email || null,
        phone: phone || null,
        role: 'pharmacist',
        postcode: postcode || null,
        address: address || null,
        address_detail: address_detail || null,
        license_number: license_number || null,
        license_url: license_url || null,
        created_at: new Date().toISOString()
      })
    }
  }

  if (role === 'pharmacy') {
    const { data: existing } = await supabase
      .from('pharmacies')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle()

    if (!existing) {
      const {
        pharmacy_name,
        business_number,
        postcode,
        pharmacy_address,
        address_detail,
        phone
      } = meta

      await supabase.from('pharmacies').insert({
        user_id: user.id,
        owner_name: meta.name || null,
        email: user.email || null,
        phone: phone || null,
        role: 'pharmacy',
        pharmacy_name: pharmacy_name || null,
        business_number: business_number || null,
        postcode: postcode || null,
        address: pharmacy_address || null,
        address_detail: address_detail || null,
        created_at: new Date().toISOString()
      })
    }
  }
}

export async function redirectByRole(nav) {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser()

    // Helper to safely call nav whether it's useNavigate or hardNav
    const doNav = (path) => {
      // If it's a function that might crash with extra args (unlikely in JS but good to be safe)
      // or if we just want to suppress errors
      try {
        nav(path, { replace: true })
      } catch (err) {
        console.warn('Nav failed with replace option, trying simple call:', err)
        nav(path)
      }
    }

    if (!user) {
      doNav('/login')
      return
    }

    let role = null
    try {
      // Try to sync/fetch profile. If this hangs/fails, we catch it.
      role = await ensureProfileRoleFromUserMetadata()
    } catch (err) {
      console.error('Profile sync failed:', err)
      // Fallback to metadata if DB lookup fails
      role = user.user_metadata?.role
    }

    // 로그인/회원가입 이후 이동 경로를 마이페이지 기준으로 변경
    if (role === 'admin') doNav('/admin')
    else if (role === 'pharmacy') doNav('/mypage/pharmacy')
    else if (role === 'pharmacist') doNav('/mypage/pharmacist')
    else doNav('/')
  } catch (e) {
    console.error('RedirectByRole critical error:', e)
    // Absolute fallback: force reload to home
    window.location.href = '/'
  }
}


// ===============================================
// 3) 공통 훅/유틸: 로그인 체크 & 알림
// ===============================================

async function requireLoginOrToast(setToast, actionName = '이 작업') {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  if (!user) {
    setToast({
      type: 'warn',
      msg: `로그인이 필요합니다. 로그인 후 ${actionName}을 진행해 주세요.`
    })
    return null
  }
  return user
}

const Toast = ({ type = 'ok', msg, onClose }) => (
  <div
    className={`mb-4 p-3 rounded-lg text-sm border shadow ${type === 'ok'
      ? 'bg-green-50 text-green-700 border-green-200'
      : type === 'warn'
        ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
        : type === 'error'
          ? 'bg-rose-50 text-rose-700 border-rose-200'
          : 'bg-blue-50 text-blue-700 border-blue-200'
      }`}
  >
    <div className="flex items-start justify-between">
      <span>{msg}</span>
      <button onClick={onClose} className="ml-4 text-xs underline">
        닫기
      </button>
    </div>
  </div>
)

// ===============================================
// 4) 약사 화면용 캘린더/리스트 컴포넌트
// ===============================================
const PharmacistCalendar = ({ jobs, onApply }) => {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 9, 1))
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedDistrict, setSelectedDistrict] = useState('')
  const availableDistricts = selectedRegion ? REGIONS[selectedRegion] : REGIONS['']

  const daysInMonth = (d) =>
    new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  const startDay = (d) => new Date(d.getFullYear(), d.getMonth(), 1).getDay()
  const totalDays = daysInMonth(currentDate)
  const startingDayIndex = startDay(currentDate)

  const daysArray = useMemo(() => {
    const arr = []
    for (let i = 0; i < startingDayIndex; i++) arr.push(null)
    for (let i = 1; i <= totalDays; i++) arr.push(i)
    return arr
  }, [startingDayIndex, totalDays])

  const formattedMonth = `${currentDate.getFullYear()}년 ${currentDate.getMonth() + 1
    }월`

  const filteredJobsByDay = useMemo(() => {
    const map = {}
    const y = currentDate.getFullYear()
    const m = currentDate.getMonth() + 1
    const pad = (n) => String(n).padStart(2, '0')
    const monthPrefix = `${y}-${pad(m)}-`
    const list = jobs
      .filter((j) => j.date?.startsWith(monthPrefix))
      .filter((j) => {
        if (!selectedRegion) return true
        if (j.region !== selectedRegion) return false
        if (selectedDistrict && selectedDistrict !== '전체 (구)')
          return j.district === selectedDistrict
        return true
      })
    list.forEach((j) => {
      const day = Number(j.date.slice(-2))
      if (!map[day]) map[day] = []
      map[day].push(j)
    })
    return map
  }, [jobs, currentDate, selectedRegion, selectedDistrict])

  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    )
  }
  const goToPrevMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    )
  }

  return (
    <div className="bg-white p-6 rounded-xl border border-indigo-200 shadow-xl h-full min-h-[500px] flex flex-col">
      <h3 className="text-xl font-bold text-indigo-700 mb-4 border-b pb-2">
        지원 가능한 약국 근무 일정
      </h3>

      <div className="flex gap-4 mb-4 items-center">
        <span className="text-sm font-medium text-gray-700">지역 필터:</span>
        <select
          value={selectedRegion}
          onChange={(e) => {
            setSelectedRegion(e.target.value)
            setSelectedDistrict('')
          }}
          className="p-2 border border-gray-300 rounded-lg text-sm w-1/3 min-w-[120px] focus:ring-indigo-500 focus:border-indigo-500 transition"
        >
          {Object.keys(REGIONS).map((region) => (
            <option key={region} value={region}>
              {region || '전체 (시/도)'}
            </option>
          ))}
        </select>
        <select
          value={selectedDistrict}
          onChange={(e) => setSelectedDistrict(e.target.value)}
          disabled={!selectedRegion || selectedRegion === ''}
          className={`p-2 border border-gray-300 rounded-lg text-sm w-1/3 min-w-[120px] focus:ring-indigo-500 focus:border-indigo-500 transition ${!selectedRegion ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            }`}
        >
          {availableDistricts.map((d) => (
            <option key={d} value={d === '전체 (구)' ? '' : d}>
              {d}
            </option>
          ))}
        </select>
        <span className="text-xs text-gray-400 ml-2">
          * 지역을 선택하면 달력이 필터링됩니다.
        </span>
      </div>

      <div className="flex justify-between items-center mb-4">
        <button
          onClick={goToPrevMonth}
          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition"
        >
          &larr; 이전
        </button>
        <h4 className="text-lg font-bold text-gray-800">{formattedMonth}</h4>
        <button
          onClick={goToNextMonth}
          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full transition"
        >
          다음 &rarr;
        </button>
      </div>

      <div className="grid grid-cols-7 text-center font-semibold text-sm text-gray-500 border-b pb-2">
        {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
          <div
            key={d}
            className={
              d === '일' ? 'text-red-500' : d === '토' ? 'text-blue-500' : ''
            }
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 flex-grow">
        {daysArray.map((day, idx) => {
          const jobsOnDay = day ? filteredJobsByDay[day] || [] : []
          const hasJob = jobsOnDay.length > 0
          const first = jobsOnDay[0]
          return (
            <div
              key={idx}
              className={`p-1 border border-gray-100 h-20 sm:h-24 ${hasJob
                ? 'cursor-pointer bg-indigo-50 hover:bg-indigo-100 transition duration-150 relative'
                : 'bg-white'
                }`}
              onClick={() => (day && hasJob ? onApply(jobsOnDay[0]) : null)}
            >
              <span
                className={`block text-xs font-semibold ${hasJob ? 'text-indigo-800' : 'text-gray-700'
                  }`}
              >
                {day}
              </span>
              {hasJob && (
                <div className="mt-1 text-xs text-indigo-600 font-medium leading-tight px-1">
                  <p className="line-clamp-2">{first.title}</p>
                  <p className="text-[10px] text-indigo-500 mt-0.5">
                    {first.time}
                  </p>
                  <span className="absolute bottom-1 right-1 text-[8px] px-1 py-0.5 bg-indigo-500 text-white rounded-full font-bold shadow-sm">
                    {jobsOnDay.length}건
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="mt-4 text-xs text-gray-500 text-center">
        * 달력에서 셀을 클릭하면 해당 공고에 지원할 수 있습니다. (로그인 필요)
      </div>
    </div>
  )
}

const PharmacistJobList = ({ jobs, onApply, disableApply = false }) => (
  <div className="bg-white p-6 rounded-xl border border-green-200 shadow-xl">
    <h3 className="text-xl font-bold text-green-700 mb-4 border-b pb-2">
      등록된 채용 공고
    </h3>
    {jobs.length === 0 ? (
      <p className="text-gray-500 text-center py-10">
        현재 등록된 공고가 없습니다.
      </p>
    ) : (
      <ul className="space-y-4">
        {jobs.map((job) => (
          <li
            key={job.id}
            className="flex justify-between items-start p-3 border border-gray-100 bg-white rounded-lg hover:bg-green-50 transition duration-150 shadow-sm"
          >
            <div className="mr-3">
              <div className="font-semibold text-gray-800 text-sm">
                {job.title}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                {job.date} · {job.time} · {job.region} {job.district} (
                {job.location})
              </div>
            </div>
            <button
              onClick={() => !disableApply && onApply(job)}
              disabled={disableApply}
              className={`text-xs px-3 py-1 rounded-full font-semibold transition ${disableApply
                ? 'bg-gray-300 text-white cursor-not-allowed'
                : 'bg-green-500 text-white hover:bg-green-600'
                }`}
            >
              {disableApply ? '읽기 전용' : '지원하기'}
            </button>
          </li>
        ))}
        <li className="text-sm text-gray-500 text-center pt-2">
          * 지원하려면 로그인 필요
        </li>
      </ul>
    )}
  </div>
)

// ✅ 약사 마이페이지 등에서 “내가 지원한 목록”을 보여줄 때 사용
const MyApplicationsPanel = () => {
  const { user } = useAuth()
  const [apps, setApps] = useState([])

  useEffect(() => {
    ; (async () => {
      if (!user) {
        setApps([])
        return
      }
      const { data } = await supabase
        .from('applications')
        .select(
          'post_id:id, job_id, status, created_at, job_posts:job_id (title, date, time, region, district, location)'
        )
        .eq('pharmacist_id', user.id)
        .order('created_at', { ascending: false })
      setApps(data || [])
    })()
  }, [user])

  return (
    <div className="bg-white p-6 rounded-xl border border-blue-200 shadow-xl">
      <h3 className="text-lg font-bold text-blue-700 mb-3">내 지원 현황</h3>
      {!user ? (
        <p className="text-gray-500">
          로그인하면 지원 내역을 볼 수 있습니다.
        </p>
      ) : apps.length === 0 ? (
        <p className="text-gray-500">아직 지원 내역이 없습니다.</p>
      ) : (
        <ul className="space-y-3">
          {apps.map((a) => (
            <li key={a.id} className="p-3 border rounded-lg bg-white">
              <div className="text-sm font-semibold text-gray-800">
                {a.job_posts?.title || '(삭제된 공고)'}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">
                {a.job_posts?.date} · {a.job_posts?.time} ·{' '}
                {a.job_posts?.region} {a.job_posts?.district} (
                {a.job_posts?.location})
              </div>
              <div className="text-xs mt-1">
                상태:{' '}
                <span
                  className={`px-2 py-0.5 rounded-full border ${a.status === 'approved'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : a.status === 'closed'
                      ? 'bg-gray-100 text-gray-600 border-gray-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                    }`}
                >
                  {a.status}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ===============================================
// 5) 약국 화면용 보드 (Pharmacy 페이지에서 확장 가능)
// ===============================================
const ApplicantsBoard = ({ applications, onUpdateStatus, readOnly = false }) => (
  <div className="p-6 bg-white rounded-xl shadow-lg border border-purple-200 h-full">
    <h3 className="text-xl font-bold text-purple-700 mb-4 border-b pb-2">
      공고별 지원자
    </h3>
    {applications.length === 0 ? (
      <p className="text-gray-500 text-center py-8">
        아직 접수된 지원이 없습니다.
      </p>
    ) : (
      <ul className="space-y-3">
        {applications.map((app) => (
          <li key={app.id} className="p-4 border rounded-lg bg-white shadow-sm">
            <div className="flex justify-between items-start">
              <div className="text-sm">
                <div className="font-semibold text-gray-800">
                  {app.job_title || '(삭제된 공고)'}
                </div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {app.job_date} · {app.job_time} · {app.job_region}{' '}
                  {app.job_district} ({app.job_location})
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  지원자:{' '}
                  {app.pharmacist_profile?.name
                    ? `${app.pharmacist_profile.name} (${app.pharmacist_profile.email ||
                    app.pharmacist_id.slice(0, 8) + '…'
                    })`
                    : app.pharmacist_id.slice(0, 8) + '…'}
                </div>
              </div>
              <div className="text-xs">
                <span
                  className={`px-2 py-0.5 rounded-full border mr-2 ${app.status === 'approved'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : app.status === 'closed'
                      ? 'bg-gray-100 text-gray-600 border-gray-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                    }`}
                >
                  {app.status}
                </span>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              {['pending', 'approved', 'closed'].map((s) => (
                <button
                  key={s}
                  onClick={() => !readOnly && onUpdateStatus(app.id, s)}
                  disabled={readOnly}
                  className={`text-[11px] px-2 py-1 rounded border ${readOnly
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : s === 'approved'
                      ? 'bg-green-50 border-green-200 hover:bg-green-100'
                      : s === 'closed'
                        ? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
                    }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
)

// ===============================================
// 6) 인증/관리/공통 (관리자용 필터 UI 등)
// ===============================================
const FilterBar = ({ children }) => (
  <div className="mb-4 p-3 bg-white border rounded-lg shadow-sm flex flex-wrap gap-2 items-end">
    {children}
  </div>
)
const Chip = ({ children }) => (
  <span className="px-2 py-0.5 rounded-full text-xs border bg-gray-50">
    {children}
  </span>
)
const Checkbox = (props) => (
  <input type="checkbox" className="w-4 h-4 accent-blue-600" {...props} />
)

// ===== RoleGuard: Global Auth Context 사용 =====
const RoleGuard = ({ allowed, children }) => {
  const { user, role, loading } = useAuth()
  const [checking, setChecking] = useState(true)
  const [ok, setOk] = useState(false)

  useEffect(() => {
    console.log('[RoleGuard] check running. State:', { loading, userEmail: user?.email, role, allowed, checking });
    if (loading && !(user && role)) {
      console.log('[RoleGuard] Waiting for auth/role...');
      return;
    }

    if (!user) {
      console.log('[RoleGuard] No user, access denied');
      setOk(false)
      setChecking(false)
      return
    }

    if (allowed.includes(role)) {
      console.log('[RoleGuard] Access GRANTED');
      setOk(true)
      ensureRoleRecords().catch(err => console.error('Role record ensure error:', err))
    } else {
      console.log(`[RoleGuard] Access DENIED. Role '${role}' not in allowed list:`, allowed);
      setOk(false)
    }
    setChecking(false)
  }, [user, role, loading, allowed])

  if (checking || loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <p>접근 권한 확인 중입니다…</p>
        <p className="text-xs mt-2 text-gray-400">잠시만 기다려 주세요.</p>
      </div>
    )
  }

  if (!ok) {
    return (
      <div className="max-w-xl mx-auto mt-10 p-6 bg-white border rounded-xl text-center text-sm text-gray-700">
        <p className="font-semibold mb-2">접근 권한이 없습니다.</p>
        <p className="mb-1">
          이 메뉴에 접근할 수 있는 권한이 없는 계정입니다.
        </p>
        <p className="text-xs text-gray-500">
          로그인 계정의 역할(약사/약국/관리자)을 확인해 주세요.
        </p>
      </div>
    )
  }

  return children
}

// ===============================================
// 7) 메인 App
// ===============================================
// 7) 메인 App Content (Router 내부에서 실행)
function AppContent({ user, role, myPageLink }) {
  const location = useLocation()
  // Admin 경로는 Mobile Shell (max-w-lg) 제한을 해제하여 넓게 씀
  const isAdminPage = location.pathname.startsWith('/admin')

  return (
    <div className="min-h-screen flex justify-center bg-gray-100 font-sans">
      <div
        className="w-full bg-white min-h-screen shadow-2xl relative flex flex-col transition-all duration-300 max-w-lg"
      >

        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b shadow-sm">
          <div className="px-4 py-3 flex items-center justify-between">
            {/* 로고 영역 */}
            <Link to="/" className="flex items-center gap-1.5 hover:opacity-80 transition">
              <span className="text-2xl" role="img" aria-label="logo">💊</span>
              <span className="text-lg font-bold text-gray-800 tracking-tight">FLOATER</span>
            </Link>

            <div className="flex items-center gap-3">
              {/* 알림 아이콘 (Dummy) */}
              <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </button>
              <AuthNav />
            </div>
          </div>
        </header>

        {/* 메인 컨텐츠 */}
        <main className="flex-1 pb-20">
          <Routes>
            {/* 공개 페이지 */}
            <Route path="/" element={<PharmacistCalendarPage />} />
            <Route path="/jobs" element={<PharmacistJobListPage />} />

            <Route path="/pharmacy" element={<Pharmacy />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />

            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-request" element={<ResetRequest />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* ✅ 마이페이지 (역할별 분기) */}
            <Route
              path="/mypage/pharmacy"
              element={
                <RoleGuard allowed={['pharmacy']}>
                  <MyPagePharmacy />
                </RoleGuard>
              }
            />
            <Route
              path="/mypage/pharmacist"
              element={
                <RoleGuard allowed={['pharmacist']}>
                  <MyPagePharmacist />
                </RoleGuard>
              }
            />

            {/* ✅ 관리자 라우트 */}
            <Route
              path="/admin"
              element={
                <RoleGuard allowed={['admin']}>
                  <AdminLayout />
                </RoleGuard>
              }
            >
              <Route index element={<AdminReview />} />{/* 기본: 가입 승인 */}
              <Route path="reviews" element={<AdminReview />} />
              <Route path="users" element={<AdminUsers />} /> {/* ✅ 회원 관리 */}
              <Route path="jobs" element={<AdminJobs />} />
              <Route path="applications" element={<AdminApplications />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
        </main>

        {/* ✅ Mobile Bottom Navigation (Admin 페이지에서는 숨김) */}
        {!isAdminPage && <BottomNav />}

        {/* iOS PWA 설치 유도 */}
        <section className="px-4 pb-20">
          <InstallPrompt />
        </section>

      </div>
    </div >
  )
}

// 8) Root App Component
export default function App() {
  const { user, role } = useAuth()

  // 로그인 상태/역할에 따라 마이페이지 링크 결정 (Header용)
  const myPageLink =
    !user
      ? null
      : role === 'pharmacist'
        ? '/mypage/pharmacist'
        : role === 'pharmacy'
          ? '/mypage/pharmacy'
          : role === 'admin'
            ? '/admin'
            : '/'

  return (
    <HashRouter>
      <AppContent user={user} role={role} myPageLink={myPageLink} />
    </HashRouter>
  )
}

// 🔁 필요하다면 다른 파일에서 재사용할 수 있게 내보내기
export {
  PharmacistCalendar,
  PharmacistJobList,
  MyApplicationsPanel,
  ApplicantsBoard,
  FilterBar,
  Chip,
  Checkbox,
  Toast,
  ReadonlyBanner,
  PageLayout,
  InputField,
  useAuth,
  requireLoginOrToast
}
